import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import { Clock, Users, Wine, ChevronLeft, Printer } from "lucide-react";

/**
 * Phase 20 — Single recipe page.
 *
 * Renders a magazine-style layout:
 *   ┌────────────── hero (16:9) ──────────────┐
 *   │     eyebrow · cuisine · course           │
 *   │     <H1 title>                           │
 *   │     <italic subtitle>                    │
 *   │  serves · prep · cook · calories         │
 *   │  intro                                   │
 *   │  ┌─ ingredients ─┐  ┌─ method ─┐         │
 *   │  └───────────────┘  └──────────┘         │
 *   │  wine pairing strip                      │
 *
 * Emits a full schema.org/Recipe JSON-LD via SeoHead so the page is
 * eligible for Google Recipe rich results / AI Overview answers.
 */
export default function RecipeDetailPage() {
  const { slug } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Scroll the page back to the top whenever we navigate to a new recipe.
    // Without this, navigating from the bottom of the /recipes grid leaves
    // the viewport at the same scroll position (which lands the user inside
    // the detail page footer).
    window.scrollTo({ top: 0, behavior: "instant" });
    setRecipe(null); setError(null);
    api.get(`/hustle/recipes/${slug}`)
      .then(({ data }) => setRecipe(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Recipe not found"));
  }, [slug]);

  const recipeSchema = useMemo(() => {
    if (!recipe) return null;
    const ings = [];
    for (const g of recipe.ingredient_groups || []) {
      for (const i of g.items || []) ings.push(i);
    }
    const steps = [];
    for (const st of recipe.method_steps || []) {
      for (const i of st.items || []) {
        steps.push({
          "@type": "HowToStep",
          text: i,
          name: st.heading || undefined,
        });
      }
    }
    const dietToSchema = {
      vegan: "https://schema.org/VeganDiet",
      vegetarian: "https://schema.org/VegetarianDiet",
      gluten_free: "https://schema.org/GlutenFreeDiet",
      celiac: "https://schema.org/GlutenFreeDiet",
      diabetic: "https://schema.org/DiabeticDiet",
      halal: "https://schema.org/HalalDiet",
      kosher: "https://schema.org/KosherDiet",
      low_carb: "https://schema.org/LowCalorieDiet",
    };
    const suitable = (recipe.dietary || []).map((d) => dietToSchema[d]).filter(Boolean);
    return {
      "@context": "https://schema.org",
      "@type": "Recipe",
      name: recipe.title,
      description: recipe.intro || recipe.subtitle || "",
      image: recipe.hero_image_url ? [recipe.hero_image_url] : undefined,
      recipeCategory: recipe.course,
      recipeCuisine: (recipe.cuisine || "").replace(/_/g, " "),
      recipeYield: recipe.serves || undefined,
      prepTime: recipe.prep_minutes ? `PT${recipe.prep_minutes}M` : undefined,
      cookTime: recipe.cook_minutes ? `PT${recipe.cook_minutes}M` : undefined,
      totalTime: (recipe.prep_minutes || recipe.cook_minutes)
        ? `PT${(recipe.prep_minutes || 0) + (recipe.cook_minutes || 0)}M`
        : undefined,
      recipeIngredient: ings,
      recipeInstructions: steps,
      suitableForDiet: suitable.length ? suitable : undefined,
      keywords: (recipe.hashtags || []).map((h) => h.replace(/^#/, "")).join(", ") || undefined,
      author: { "@type": "Organization", name: "The Hustle Plate" },
    };
  }, [recipe]);

  if (error) {
    return (
      <div data-testid="recipe-error" className="bg-[#FFFAF0] min-h-screen py-20 text-center">
        <p className="font-serif-display text-3xl">Recipe not found</p>
        <p className="mt-2 text-sm text-[#6B6157]">{error}</p>
        <Link to="/recipes" className="inline-block mt-4 text-[#C05746] underline">
          ← Back to the cookbook
        </Link>
      </div>
    );
  }

  if (!recipe) {
    return <div className="p-10 text-sm text-[#6B6157]">Loading recipe…</div>;
  }

  return (
    <article data-testid="recipe-detail" className="bg-[#FFFAF0] min-h-screen">
      <SeoHead
        scope="page"
        title={`${recipe.title} | Recipe — The Hustle Plate`}
        description={recipe.intro || recipe.subtitle || `${recipe.title} — a recipe from The Hustle Plate.`}
        extras={recipeSchema ? [recipeSchema] : []}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Link
          to="/recipes"
          data-testid="recipe-back"
          className="inline-flex items-center gap-1 text-sm text-[#6B6157] hover:text-[#C05746]"
        >
          <ChevronLeft className="h-4 w-4" /> Back to the cookbook
        </Link>
      </div>

      {/* HEADER */}
      <header className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <div className="text-xs uppercase tracking-[0.28em] text-[#C05746] font-medium">
          {(recipe.cuisine || "").replace(/_/g, " ")} · {recipe.course}
        </div>
        <h1 className="mt-3 font-serif-display text-5xl sm:text-6xl lg:text-7xl text-[#1A1410] leading-[0.98]">
          {recipe.title}
        </h1>
        {recipe.subtitle && (
          <p className="mt-3 italic text-xl sm:text-2xl text-[#6B6157]">{recipe.subtitle}</p>
        )}
      </header>

      {/* HERO */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div
          className="aspect-[16/9] rounded-2xl bg-cover bg-center bg-[#F6EFE0] shadow-sm"
          style={recipe.hero_image_url ? { backgroundImage: `url("${recipe.hero_image_url}")` } : {}}
        />
      </div>

      {/* META STRIP */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <div className="grid sm:grid-cols-4 gap-4 py-4 border-y border-[#E4DBC6]">
          <Meta label="Serves" value={recipe.serves || "—"} icon={Users} />
          <Meta label="Prep" value={recipe.prep_minutes ? `${recipe.prep_minutes} min` : "—"} icon={Clock} />
          <Meta label="Cook" value={recipe.cook_minutes ? `${recipe.cook_minutes} min` : "—"} icon={Clock} />
          <Meta label="Calories" value={recipe.calories || "—"} icon={null} />
        </div>
      </div>

      {recipe.intro && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <p className="italic text-lg text-[#6B6157] leading-relaxed">{recipe.intro}</p>
        </div>
      )}

      {/* BODY — ingredients + method side by side */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="grid md:grid-cols-5 gap-8 lg:gap-12">
          <aside className="md:col-span-2">
            <h2 className="text-xs uppercase tracking-[0.28em] text-[#A88842] font-bold border-b border-[#A88842] pb-2">
              Ingredients
            </h2>
            <div className="mt-4 space-y-5">
              {(recipe.ingredient_groups || []).map((g, gi) => (
                <div key={gi} data-testid={`ingredient-group-${gi}`}>
                  {g.heading && (
                    <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#C05746] font-bold mb-2">
                      {g.heading}
                    </h3>
                  )}
                  <ul className="space-y-1.5">
                    {(g.items || []).map((it, ii) => (
                      <li
                        key={ii}
                        className="text-[15px] text-[#1A1410] py-1.5 border-b border-dotted border-[#E4DBC6]"
                      >
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </aside>

          <section className="md:col-span-3">
            <h2 className="text-xs uppercase tracking-[0.28em] text-[#A88842] font-bold border-b border-[#A88842] pb-2">
              Method
            </h2>
            <div className="mt-4 space-y-6">
              {(recipe.method_steps || []).map((st, si) => (
                <div key={si} data-testid={`method-step-${si}`}>
                  {st.heading && (
                    <h3 className="text-[11px] uppercase tracking-[0.22em] text-[#C05746] font-bold mb-2">
                      {st.heading}
                    </h3>
                  )}
                  <ol className="list-decimal pl-5 space-y-2.5 text-[15px] leading-relaxed text-[#1A1410]">
                    {(st.items || []).map((it, ii) => (
                      <li key={ii}>{it}</li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {recipe.wine_pairing && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
          <div className="rounded-xl border-l-4 border-[#C05746] bg-[#F6EFE0] px-5 py-4 flex items-start gap-3">
            <Wine className="h-5 w-5 text-[#C05746] mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#C05746] font-bold">Wine pairing</p>
              <p className="mt-1 text-[#1A1410]">{recipe.wine_pairing}</p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 flex flex-wrap items-center justify-between gap-4">
        <Link to="/recipes" className="text-[#C05746] hover:underline text-sm">
          ← Browse more recipes
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="recipe-print"
          className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white px-4 py-2 text-sm hover:bg-black/5"
        >
          <Printer className="h-4 w-4" /> Print this recipe
        </button>
      </div>
    </article>
  );
}

function Meta({ label, value, icon: Icon }) {
  return (
    <div className="flex items-baseline gap-2">
      {Icon && <Icon className="h-4 w-4 text-[#6B6157]" />}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6157] font-medium">{label}</div>
        <div className="font-serif-display text-xl text-[#1A1410]">{value}</div>
      </div>
    </div>
  );
}
